import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  isPartnerWebviewSession,
  partnerHomePath,
} from "utils/partnerSession.util";
import { useAppSelector } from "redux/hooks";
import LocalStorage from "utils/localstorage.util";
import DateService from "utils/date.util";
import useDirection from "hooks/useDirection";
import useBookingTimeFormat from "hooks/useBookingTimeFormat";
import { getHourlyDuration } from "utils/aggregatorHourlyPricing.util";
import { goBack as goBackTo } from "utils/navigation.util";

/**
 * One header for every step of the booking flow.
 *
 * It used to print the step's own name — "Select Vehicle Class", "Passenger
 * details", "Review Booking" — which each page already states in its own
 * heading, and which tells you nothing you did not just choose. It now shows
 * the journey being booked, so the same fact stays on screen from the fare
 * through to payment, and the progress bar says which step you are on.
 *
 * The pill is not a control. An "Edit" affordance sat here briefly and was
 * wrong twice over: it competes with the back button beside it, which already
 * goes back a step, and on the review screen "edit" would mean something
 * different again. The back button is the only way out of a step.
 */

type BookingPlace = { value?: string; details?: { name?: string } };

const totalSteps = 3;

export default function FormHeader({ page, prevPage }: any) {
  const navigate = useNavigate();
  const { t } = useTranslation("translation");
  const direction = useDirection();
  const { format: timeFormat } = useBookingTimeFormat();
  const [payload, setPayload] = useState<any>(null);
  const [routeNames, setRouteNames] = useState<any>(null);

  useEffect(() => {
    setPayload(LocalStorage.getItem("BookingFormPayload"));
    setRouteNames(LocalStorage.getItem("BookingRouteNames"));
  }, []);

  const formValues = useAppSelector(
    (state) => state.form?.PickUpForm?.values
  ) as
    | {
        pickup?: BookingPlace;
        destination?: BookingPlace;
        duration?: string | number;
      }
    | undefined;

  const formDistance = useAppSelector(
    (state) => state.form?.SearchTripForm?.values?.distance
  );
  const distance = payload?.distance ?? formDistance;
  const isHourly = payload?.trip_type === "hourly";
  const hourlyDuration = getHourlyDuration(payload, formValues);

  /* Place name first, the typed address as a fallback, and the saved
     search as the last resort. The live form is empty after a reload or a
     return from payment, but the search that priced this booking is stored
     (BookingFormPayload, with pickup and destination as addresses), so the
     route stays on screen at every step instead of falling back to just the
     date and distance. */
  const label = (place?: BookingPlace) =>
    place?.details?.name || place?.value || "";
  /* The saved search keeps the full address; its first part is the place's
     name ("King Khalid International Airport, Terminal 4, …"), which is
     what the pill shows when the live form has it. */
  const savedPlace = (value: unknown) =>
    typeof value === "string" ? value.split(/[,،]/)[0].trim() : "";
  const from =
    label(formValues?.pickup) ||
    routeNames?.pickup ||
    savedPlace(payload?.pickup);
  const to = isHourly
    ? hourlyDuration
      ? `${hourlyDuration} ${t("hours")}`
      : ""
    : label(formValues?.destination) ||
      routeNames?.destination ||
      savedPlace(payload?.destination);
  /* The arrow is a relation between two places, so it only renders when
     there are two. With one end missing — a direct load before the form
     state rehydrates — the pill showed a bare "→ 2 Hours" pointing at
     nothing; it now shows whichever end it actually has. */
  const hasRoute = Boolean(from || to);
  const hasBothEnds = Boolean(from && to);

  const meta = [
    DateService.getShortDateString(payload?.time_stamp),
    DateService.getDisplayTimeString(payload?.time_stamp, timeFormat),
    !isHourly && distance ? `${distance} ${t("km")}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const homePath = isPartnerWebviewSession() ? partnerHomePath() : "/";
  const goBack = () => (page === 1 ? goBackTo(navigate, homePath) : prevPage());

  return (
    <header className="flow-header">
      <Container maxWidth="lg">
        <div className="flow-header__row">
          <button
            type="button"
            className="flow-header__back"
            aria-label={String(t("back"))}
            onClick={goBack}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M15 5l-7 7 7 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {page === 3 && (
            <h1 className="flow-header__review-title">
              {t("reviewBooking")}
            </h1>
          )}

          <div className="flow-header__journey">
            <span className="flow-header__pin" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path
                  d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="12"
                  cy="10"
                  r="2.6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
              </svg>
            </span>
            <span className="flow-header__text">
              {hasRoute && (
                <b>
                  {from && <span className="a">{from}</span>}
                  {hasBothEnds && <i aria-hidden="true">→</i>}
                  {to && <span className="z">{to}</span>}
                </b>
              )}
              {meta && <small>{meta}</small>}
            </span>
          </div>
        </div>

        {/* The three-segment stepper is gone. It marked progress through a
            flow whose steps already announce themselves — the journey pill
            says what is being booked, and each screen names what it wants —
            so it was a second, quieter answer to a question nobody was
            asking, taking a row of height on every step. */}
      </Container>
    </header>
  );
}
